import { useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { TransporterPhoto, TransporterVideo } from '@otw/models'

interface TransporterPhotoStatusHook {
  photos: TransporterPhoto[]
  videos: TransporterVideo[]
  isLoading: boolean
  error: Error | null
  getVehiclePhotos: (packageId: string, vehicleId: string, deliveryStatus?: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair') => TransporterPhoto[]
  getVehicleVideos: (packageId: string, vehicleId: string, deliveryStatus?: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair') => TransporterVideo[]
  getPhotoCount: (packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair') => number
  getVideoCount: (packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair') => number
  getMediaCount: (packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair') => { photos: number, videos: number, total: number }
  refetch: () => void
}

// Fetch photos for a single package
const fetchPackagePhotos = async (packageId: string): Promise<TransporterPhoto[]> => {
  const allPhotosSnapshot = await getDocs(collection(db, 'transporter_packages', packageId, 'photos'))
  return allPhotosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TransporterPhoto))
}

// Fetch photos for multiple packages
const fetchMultiplePackagePhotos = async (packageIds: string[]): Promise<TransporterPhoto[]> => {
  const allPhotosPromises = packageIds.map(packageId => fetchPackagePhotos(packageId))
  const allPhotosArrays = await Promise.all(allPhotosPromises)
  return allPhotosArrays.flat()
}

// Fetch videos for a single package
const fetchPackageVideos = async (packageId: string): Promise<TransporterVideo[]> => {
  const allVideosSnapshot = await getDocs(collection(db, 'transporter_packages', packageId, 'videos'))
  return allVideosSnapshot.docs.map(doc => ({ 
    id: doc.id, 
    ...doc.data(),
    packageId // Add packageId to each video for proper key generation
  } as TransporterVideo & { packageId: string }))
}

// Fetch videos for multiple packages
const fetchMultiplePackageVideos = async (packageIds: string[]): Promise<TransporterVideo[]> => {
  const allVideosPromises = packageIds.map(packageId => fetchPackageVideos(packageId))
  const allVideosArrays = await Promise.all(allVideosPromises)
  return allVideosArrays.flat()
}

// Process photos by package-vehicle combination and delivery status
const processPhotosByVehicle = (
  allPhotos: TransporterPhoto[]
): Map<string, { preDelivery: TransporterPhoto[], postDelivery: TransporterPhoto[], preRepair: TransporterPhoto[], postRepair: TransporterPhoto[] }> => {
  const photosByVehicle = new Map<string, { preDelivery: TransporterPhoto[], postDelivery: TransporterPhoto[], preRepair: TransporterPhoto[], postRepair: TransporterPhoto[] }>()
  
  for (const photo of allPhotos) {
    // Use packageId-vehicleId as the key to distinguish between packages
    const key = `${photo.packageId}-${photo.vehicleId}`
    if (!photosByVehicle.has(key)) {
      photosByVehicle.set(key, { preDelivery: [], postDelivery: [], preRepair: [], postRepair: [] })
    }
    
    const vehiclePhotos = photosByVehicle.get(key)!
    if (photo.deliveryStatus === 'preDelivery') {
      vehiclePhotos.preDelivery.push(photo)
    } else if (photo.deliveryStatus === 'postDelivery') {
      vehiclePhotos.postDelivery.push(photo)
    } else if (photo.deliveryStatus === 'preRepair') {
      vehiclePhotos.preRepair.push(photo)
    } else if (photo.deliveryStatus === 'postRepair') {
      vehiclePhotos.postRepair.push(photo)
    }
  }

  return photosByVehicle
}

// Process videos by package-vehicle combination and delivery status
const processVideosByVehicle = (
  allVideos: (TransporterVideo & { packageId?: string })[]
): Map<string, { preDelivery: TransporterVideo[], postDelivery: TransporterVideo[], preRepair: TransporterVideo[], postRepair: TransporterVideo[] }> => {
  const videosByVehicle = new Map<string, { preDelivery: TransporterVideo[], postDelivery: TransporterVideo[], preRepair: TransporterVideo[], postRepair: TransporterVideo[] }>()
  
  for (const video of allVideos) {
    // Use packageId-vehicleId as the key to distinguish between packages
    // packageId should be added during fetch, but fallback to extracting from storage path if missing
    let packageId = (video as any).packageId
    if (!packageId && video.storagePath) {
      const packageIdMatch = video.storagePath.match(/transporter_packages\/([^/]+)\//)
      packageId = packageIdMatch ? packageIdMatch[1] : ''
    }
    const key = packageId ? `${packageId}-${video.vehicleId}` : video.vehicleId
    
    if (!videosByVehicle.has(key)) {
      videosByVehicle.set(key, { preDelivery: [], postDelivery: [], preRepair: [], postRepair: [] })
    }
    
    const vehicleVideos = videosByVehicle.get(key)!
    if (video.deliveryStatus === 'preDelivery') {
      vehicleVideos.preDelivery.push(video)
    } else if (video.deliveryStatus === 'postDelivery') {
      vehicleVideos.postDelivery.push(video)
    } else if (video.deliveryStatus === 'preRepair') {
      vehicleVideos.preRepair.push(video)
    } else if (video.deliveryStatus === 'postRepair') {
      vehicleVideos.postRepair.push(video)
    }
  }

  return videosByVehicle
}

export const useTransporterPhotoStatus = (packageIdOrIds: string | string[], vehicleIds: string[]): TransporterPhotoStatusHook => {
  // Memoize the vehicleIds to prevent unnecessary re-renders
  const memoizedVehicleIds = useMemo(() => vehicleIds, [vehicleIds.join(',')])
  
  // Normalize packageIdOrIds to always be an array
  const packageIds = useMemo(() => 
    Array.isArray(packageIdOrIds) ? packageIdOrIds : [packageIdOrIds], 
    [packageIdOrIds]
  )
  
  // Create query key based on whether we have single or multiple packages
  const queryKey = useMemo(() => {
    if (packageIds.length === 1) {
      return ['transporter_packages', packageIds[0], 'photos']
    } else {
      return ['transporter_packages', 'multiple', packageIds.sort().join(','), 'photos']
    }
  }, [packageIds])

  // Query for package photos using React Query
  const { data: allPhotos = [], isLoading: isLoadingPhotos, error: photoError, refetch: refetchPhotos } = useQuery({
    queryKey,
    queryFn: () => {
      if (packageIds.length === 1) {
        return fetchPackagePhotos(packageIds[0])
      } else {
        return fetchMultiplePackagePhotos(packageIds)
      }
    },
    enabled: packageIds.length > 0 && packageIds.every(id => !!id),
  })

  // Create query key for videos
  const videoQueryKey = useMemo(() => {
    if (packageIds.length === 1) {
      return ['transporter_packages', packageIds[0], 'videos']
    } else {
      return ['transporter_packages', 'multiple', packageIds.sort().join(','), 'videos']
    }
  }, [packageIds])

  // Query for package videos using React Query
  const { data: allVideos = [], isLoading: isLoadingVideos, error: videoError, refetch: refetchVideos } = useQuery({
    queryKey: videoQueryKey,
    queryFn: () => {
      if (packageIds.length === 1) {
        return fetchPackageVideos(packageIds[0])
      } else {
        return fetchMultiplePackageVideos(packageIds)
      }
    },
    enabled: packageIds.length > 0 && packageIds.every(id => !!id),
  })

  // Process photos by vehicle and delivery status
  const photosByVehicle = useMemo(() => {
    if (packageIds.length === 0 || memoizedVehicleIds.length === 0) {
      return new Map()
    }
    return processPhotosByVehicle(allPhotos)
  }, [packageIds, memoizedVehicleIds, allPhotos])
  
  // Process videos by vehicle and delivery status
  const videosByVehicle = useMemo(() => {
    if (packageIds.length === 0 || memoizedVehicleIds.length === 0) {
      return new Map()
    }
    return processVideosByVehicle(allVideos)
  }, [packageIds, memoizedVehicleIds, allVideos])

  const getVehiclePhotos = useCallback((packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair' = 'preDelivery'): TransporterPhoto[] => {
    const key = `${packageId}-${vehicleId}`
    const vehiclePhotos = photosByVehicle.get(key)
    if (!vehiclePhotos) return []
    
    let photos: TransporterPhoto[] = []
    if (deliveryStatus === 'preDelivery') {
      photos = vehiclePhotos.preDelivery
    } else if (deliveryStatus === 'postDelivery') {
      photos = vehiclePhotos.postDelivery
    } else if (deliveryStatus === 'preRepair') {
      photos = vehiclePhotos.preRepair
    } else if (deliveryStatus === 'postRepair') {
      photos = vehiclePhotos.postRepair
    }
    
    // Sort by upload time (newest first)
    return photos.sort((a: TransporterPhoto, b: TransporterPhoto) => {
      const aTime = a.uploadedAt?.toMillis?.() || 0
      const bTime = b.uploadedAt?.toMillis?.() || 0
      return bTime - aTime
    })
  }, [photosByVehicle])

  const getVehicleVideos = useCallback((packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair' = 'preDelivery'): TransporterVideo[] => {
    // Extract packageId from storage path if not provided directly
    // For videos, we need to match by packageId extracted from storage path
    const key = `${packageId}-${vehicleId}`
    const vehicleVideos = videosByVehicle.get(key)
    if (!vehicleVideos) return []
    
    let videos: TransporterVideo[] = []
    if (deliveryStatus === 'preDelivery') {
      videos = vehicleVideos.preDelivery
    } else if (deliveryStatus === 'postDelivery') {
      videos = vehicleVideos.postDelivery
    } else if (deliveryStatus === 'preRepair') {
      videos = vehicleVideos.preRepair
    } else if (deliveryStatus === 'postRepair') {
      videos = vehicleVideos.postRepair
    }
    
    // Sort by upload time (newest first)
    return videos.sort((a: TransporterVideo, b: TransporterVideo) => {
      const aTime = a.uploadedAt?.toMillis?.() || 0
      const bTime = b.uploadedAt?.toMillis?.() || 0
      return bTime - aTime
    })
  }, [videosByVehicle])

  const getPhotoCount = useCallback((packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair'): number => {
    const key = `${packageId}-${vehicleId}`
    const vehiclePhotos = photosByVehicle.get(key)
    if (!vehiclePhotos) return 0
    
    if (deliveryStatus === 'preDelivery') {
      return vehiclePhotos.preDelivery.length
    } else if (deliveryStatus === 'postDelivery') {
      return vehiclePhotos.postDelivery.length
    } else if (deliveryStatus === 'preRepair') {
      return vehiclePhotos.preRepair.length
    } else if (deliveryStatus === 'postRepair') {
      return vehiclePhotos.postRepair.length
    }
    return 0
  }, [photosByVehicle])

  const getVideoCount = useCallback((packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair'): number => {
    const key = `${packageId}-${vehicleId}`
    const vehicleVideos = videosByVehicle.get(key)
    if (!vehicleVideos) return 0
    
    if (deliveryStatus === 'preDelivery') {
      return vehicleVideos.preDelivery.length
    } else if (deliveryStatus === 'postDelivery') {
      return vehicleVideos.postDelivery.length
    } else if (deliveryStatus === 'preRepair') {
      return vehicleVideos.preRepair.length
    } else if (deliveryStatus === 'postRepair') {
      return vehicleVideos.postRepair.length
    }
    return 0
  }, [videosByVehicle])

  const getMediaCount = useCallback((packageId: string, vehicleId: string, deliveryStatus: 'preDelivery' | 'postDelivery' | 'preRepair' | 'postRepair') => {
    const photoCount = getPhotoCount(packageId, vehicleId, deliveryStatus)
    const videoCount = getVideoCount(packageId, vehicleId, deliveryStatus)
    
    return {
      photos: photoCount,
      videos: videoCount,
      total: photoCount + videoCount
    }
  }, [getPhotoCount, getVideoCount])

  const refetch = useCallback(async () => {
    await Promise.all([refetchPhotos(), refetchVideos()])
  }, [refetchPhotos, refetchVideos])

  return {
    photos: allPhotos,
    videos: allVideos,
    isLoading: isLoadingPhotos || isLoadingVideos,
    error: photoError || videoError,
    getVehiclePhotos,
    getVehicleVideos,
    getPhotoCount,
    getVideoCount,
    getMediaCount,
    refetch
  }
}
